import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Check, Loader2, AlertTriangle, Users } from "lucide-react";
import { format, parseISO } from "date-fns";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import api from "../../services/api";
import AnnouncementsMarkdown from "../AnnouncementsMarkdown";

const PRIORIDADES = {
	1: { rotulo: "Normal", variante: "outline-solid" },
	2: { rotulo: "Importante", variante: "secondary" },
	3: { rotulo: "Urgente", variante: "destructive" },
};

const AnnouncementsAutoPopup = ({ openFromLayout, triggerRefetch }) => {
	const { user } = useContext(AuthContext);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [announcements, setAnnouncements] = useState([]);
	const ackingRef = useRef(new Set());
	const [ackingAll, setAckingAll] = useState(false);
	const mounted = useRef(false);

	const fetchUnread = useCallback(async () => {
		if (!user?.id) return;
		setLoading(true);
		try {
			const { data } = await api.get("/announcements-unread");
			const lista = Array.isArray(data?.announcements) ? data.announcements : [];
			setAnnouncements(lista);
			if (lista.length > 0) {
				setOpen(true);
			}
		} catch (_) {
			// Falha temporária.
		}
		setLoading(false);
	}, [user?.id]);

	// Primeiro login: só dispara uma vez, evitando reabrir no F5 se o usuário
	// já marcou como ciente (mesmo que o layout remontar a cada tela).
	useEffect(() => {
		if (!user?.id) return;
		if (mounted.current) return;
		mounted.current = true;
		const t = setTimeout(() => fetchUnread(), 350);
		return () => clearTimeout(t);
	}, [user?.id, fetchUnread]);

	useEffect(() => {
		if (!openFromLayout) return;
		fetchUnread();
	}, [openFromLayout, fetchUnread]);

	useEffect(() => {
		if (!triggerRefetch) return;
		fetchUnread();
	}, [triggerRefetch, fetchUnread]);

	useEffect(() => {
		if (!user?.id) return;
		const socket = openSocket();
		const handler = (payload) => {
			if (!payload) return;
			if (payload.action === "refreshBadge") {
				fetchUnread();
			}
		};
		socket.on("announcements", handler);
		return () => socket.off("announcements", handler);
	}, [user?.id, fetchUnread]);

	const ackOne = useCallback(async (id) => {
		if (!user?.id) return;
		if (ackingRef.current.has(id)) return;
		ackingRef.current.add(id);
		try {
			await api.post(`/announcements/${id}/ack`);
			setAnnouncements((prev) => {
				const nova = prev.filter((a) => +a.id !== +id);
				if (nova.length === 0) setOpen(false);
				return nova;
			});
		} catch (_) {
			fetchUnread();
		} finally {
			ackingRef.current.delete(id);
		}
	}, [user?.id, fetchUnread]);

	const ackAll = useCallback(async () => {
		if (!user?.id) return;
		setAckingAll(true);
		try {
			await api.post("/announcements-ack-all");
			setAnnouncements([]);
			setOpen(false);
		} catch (_) {
			fetchUnread();
		}
		setAckingAll(false);
	}, [user?.id, fetchUnread]);

	return (
		<Dialog open={open} onOpenChange={(abrir) => !abrir && setOpen(false)}>
			<DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-primary" />
						Avisos internos
						{announcements.length > 0 && (
							<Badge variant="destructive" className="ml-1">
								{announcements.length}
							</Badge>
						)}
					</DialogTitle>
					<DialogDescription>
						Confira as novidades da equipe antes de começar o atendimento.
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[62vh] overflow-y-auto pr-1">
					{loading && (
						<p className="py-10 text-center text-sm text-muted-foreground">
							Carregando avisos…
						</p>
					)}
					{!loading && announcements.length === 0 && (
						<p className="py-10 text-center text-sm text-muted-foreground">
							Nenhum aviso novo no momento.
						</p>
					)}
					{!loading && announcements.map((a) => {
						const p = PRIORIDADES[a.priority] || PRIORIDADES[1];
						const acking = ackingRef.current.has(a.id);
						const eGlobal = Boolean(a.isGlobal);
						return (
							<article
								key={a.id}
								className={cn(
									"mb-3 flex flex-col gap-2 rounded-lg border bg-card p-3 last:mb-0",
									eGlobal && "border-dashed border-primary/50 ring-1 ring-primary/10 bg-destructive/[0.02]"
								)}
							>
								<header className="flex items-start justify-between gap-2">
									<div className="min-w-0 flex-1">
										<div className="mb-1 flex flex-wrap items-center gap-1.5">
											{!eGlobal && (
												<Badge
													variant="default"
													className="h-5 gap-1 bg-sky-600/90 px-2 text-[10px] text-sky-50 hover:bg-sky-600"
												>
													<Users className="h-3 w-3" />
													AVISO INTERNO EQUIPE
												</Badge>
											)}
											<Badge variant={p.variante} className="h-5 text-[10px]">
												{p.rotulo}
											</Badge>
										</div>
										<h3 className="truncate font-semibold leading-tight">
											{a.title}
										</h3>
										<div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
											<span>{eGlobal ? "Equipe Perabit" : a.user?.name || "Sistema"}</span>
											<span>·</span>
											{a.createdAt && (
												<span>{format(parseISO(a.createdAt), "dd/MM HH:mm")}</span>
											)}
										</div>
									</div>
									<Button
										variant="outline"
										size="sm"
										className="h-7 shrink-0 gap-1 text-xs"
										onClick={() => ackOne(a.id)}
										disabled={acking}
									>
										{acking ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<Check className="h-3.5 w-3.5" />
										)}
										Ciente
									</Button>
								</header>
								<div className="rounded-md bg-muted/40 p-2">
									<AnnouncementsMarkdown>{a.text}</AnnouncementsMarkdown>
								</div>
							</article>
						);
					})}
				</div>

				<DialogFooter>
					<Button
						variant="ghost"
						onClick={() => setOpen(false)}
						disabled={ackingAll}
					>
						Ver depois
					</Button>
					<Button onClick={ackAll} disabled={ackingAll || announcements.length === 0}>
						{ackingAll ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<Check className="mr-2 h-4 w-4" />
						)}
						Ciente de tudo
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AnnouncementsAutoPopup;
